/** Market Scanner — filters aligned with watchlist verdicts + confluence. */

import React, { useEffect, useState } from "react";
import { SECTOR_ROTATION_TICKER_MAP, SECTOR_TICKER_PLAYS } from "./sectorRotation.js";
import {
  SCANNER_SECTOR_OPTIONS,
  SCANNER_SECTOR_ETF,
  filterTickersByRotationSector,
} from "./scannerUniverse.js";
import {
  matchesVerdictProfile,
  scannerDisplayVerdict,
  rankScannerResult,
  isExtendedVerdict,
  SCANNER_PRESETS,
  applyScannerPreset,
} from "./scannerFilters.js";
import { watchlistConfluenceCell } from "./confluenceScore.js";
import { evaluateNearPrimeSetup } from "./primeSetupFlag.js";
import { isScoringV2Result } from "./resolveScannerAction.js";
import { longTermStatusPill } from "./longTermChecklist.js";
import { getEarningsRowPill } from "./earningsDisplay.js";
import { MaStackPills, matchesAboveAllMas, matchesTestingEma50 } from "./maStackUI.jsx";

export { SCANNER_SECTOR_OPTIONS, SCANNER_SECTOR_ETF };

export const SIGNAL_TYPE_OPTIONS = [
  { id: "ALL", label: "All Signals", emoji: "" },
  { id: "SWING_LONG", label: "Swing Long Setups", emoji: "🟢" },
  { id: "BOTTOM_CONFIRMED", label: "Bottom Confirmed", emoji: "🔵" },
  { id: "BOTTOM_FORMING", label: "Bottom Forming", emoji: "👁" },
  { id: "TOP_CONFIRMED", label: "Top Confirmed", emoji: "🔴" },
  { id: "TOP_FORMING", label: "Top Forming", emoji: "⚠" },
  { id: "WAIT", label: "Wait / No Signal", emoji: "⚪" },
];

export const SETUP_TYPE_OPTIONS = [
  { id: "vcp", label: "VCP Breakout", match: r => !!r?.module1?.setupB },
  { id: "pullback", label: "Pullback to EMA20/SMA50", match: r => !!r?.module1?.setupA },
  { id: "doubleBottom", label: "Double Bottom (W-Pattern)", match: r => !!(r?.module2?.setupA?.active || r?.module2?.setupA?.triggered) },
  { id: "higherLow", label: "Higher Low Confirmed", match: r => !!(r?.module2?.setupB?.active || r?.module2?.setupB?.triggered) },
  { id: "hammer", label: "Hammer at Support", match: r => !!r?.module2?.setupC?.active },
  { id: "doubleTop", label: "Double Top (M-Pattern)", match: r => !!(r?.module3?.setupA?.active || r?.module3?.setupA?.triggered) },
  { id: "lowerHigh", label: "Lower High Confirmed", match: r => !!(r?.module3?.setupB?.active || r?.module3?.setupB?.triggered) },
  { id: "shootingStar", label: "Shooting Star at Resistance", match: r => !!r?.module3?.setupC?.active },
];

export const QUICK_FILTER_LEAD = [
  { id: "ALL", label: "All" },
  { id: "CONSIDER", label: "Consider" },
  { id: "NEW", label: "Not on WL" },
];

export const QUICK_FILTER_SIGNAL = [
  { id: "CALLS", label: "Calls" },
  { id: "DIPS", label: "Dips" },
  { id: "BOTTOM", label: "Bottom" },
  { id: "TOP", label: "Top" },
  { id: "WATCH", label: "Watch" },
  { id: "WAIT", label: "Wait" },
];

export const QUICK_FILTER_PRIME_ID = "PRIME";

/** @deprecated use QUICK_FILTER_LEAD + QUICK_FILTER_SIGNAL */
export const QUICK_FILTER_OPTIONS = [
  ...QUICK_FILTER_LEAD,
  { id: QUICK_FILTER_PRIME_ID, label: "Prime" },
  ...QUICK_FILTER_SIGNAL,
];

const PRIME_CONFLUENCE_TIER = {
  DONCHIAN_BREAKOUT_CONFIRMED: 4,
  FIB_CONFLUENCE_LONG: 3,
  FIB_BOUNCE_DIVERGENCE: 2,
};

export function getScannerConfluenceType(result) {
  const c = result?.confluence;
  if (c?.active && c.setupType) return c.setupType;
  return c?.type ?? result?.confluenceType ?? null;
}

export function primeConfluenceSortValue(result) {
  const t = getScannerConfluenceType(result);
  const tier = PRIME_CONFLUENCE_TIER[t] ?? 0;
  const score = typeof result?.buyScore === "number" ? result.buyScore : -Infinity;
  return tier * 1000 + score;
}

export const MIN_STRENGTH_OPTIONS = [
  { value: 0, label: "Any" },
  { value: 50, label: "50%+" },
  { value: 65, label: "65%+" },
  { value: 75, label: "75%+" },
];

export const SCANNER_SORT_OPTIONS = [
  { col: "rank", label: "Best setups" },
  { col: "buyScore", label: "Score" },
  { col: "rsDuringDrop", label: "Holding up best" },
  { col: "signalStrength", label: "Strength" },
  { col: "cmf", label: "CMF" },
  { col: "rsi", label: "RSI" },
  { col: "pctChange", label: "% Change" },
  { col: "t1Upside", label: "T1 Upside" },
  { col: "maStack", label: "% vs EMA50" },
];

export const SCANNER_TABLE_COLUMNS = [
  { id: "symbol", label: "Ticker", width: 108, sortable: true },
  { id: "signal", label: "Verdict", width: 130, sortable: true },
  { id: "buyScore", label: "Score", width: 52, sortable: true },
  { id: "gate", label: "Entry", width: 58, sortable: true },
  { id: "maStack", label: "MA", width: 52, sortable: true },
  { id: "rr1", label: "R:R", width: 48, sortable: true },
  { id: "confluence", label: "Conf", width: 72, sortable: true },
  { id: "price", label: "Price", width: 76, sortable: true },
  { id: "cmf", label: "CMF", width: 52, sortable: true },
  { id: "rsi", label: "RSI", width: 42, sortable: true },
  { id: "sector", label: "Sector", width: 78, sortable: true },
  { id: "t1Upside", label: "T1", width: 72, sortable: true },
  { id: "add", label: "", width: 56, sortable: false },
];

export const DEFAULT_SCANNER_FILTERS = {
  signalTypes: ["ALL"],
  quickFilter: "ALL",
  verdictProfile: "ALL",
  setupTypes: [],
  validation: {
    cmf: false,
    rsi: false,
    volume: false,
    obv: false,
    preFilter: false,
  },
  minStrength: 0,
  context: {
    bullTide: false,
    qqqBullish: false,
    sectorLeaders: false,
    excludeEarnings14: false,
    minPrice5: true,
    hideExtended: true,
    aboveAllMas: false,
    testingEma50: false,
  },
  sector: "All Sectors",
  theme: "All Themes",
  universeMode: "core",
};

export const SECTOR_SCAN_PRESET = {
  signalTypes: ["SWING_LONG", "BOTTOM_CONFIRMED", "BOTTOM_FORMING"],
  validation: { cmf: true, rsi: false, volume: false, obv: false, preFilter: false },
  minStrength: 50,
  context: { bullTide: false, qqqBullish: false, sectorLeaders: false, excludeEarnings14: false, minPrice5: true },
};

export function getScannerBorderColor(signal) {
  const map = {
    SWING_LONG: "#16a34a",
    BOTTOM_CONFIRMED: "#0284c7",
    BOTTOM_FORMING: "#0891b2",
    TOP_CONFIRMED: "#ef4444",
    TOP_FORMING: "#f97316",
    WAIT: "#64748b",
  };
  return map[signal] || map.WAIT;
}

export function normalizeScannerSignal(result) {
  if (isScoringV2Result(result)) {
    const label = scannerDisplayVerdict(result).verdict || "";
    if (label.includes("CALLS")) return "SWING_LONG";
    if (label.includes("PUTS") || /TOP FORMING/i.test(label)) return "TOP_CONFIRMED";
    if (/BOTTOM/i.test(label)) return "BOTTOM_CONFIRMED";
    if (label.startsWith("WATCH")) return "SWING_LONG";
    if (/^DIP/i.test(label) || label.includes("DIP")) return "SWING_LONG";
    return "WAIT";
  }
  const sig = result?.signal;
  if (!sig || sig === "NO_SIGNAL") return "WAIT";
  return sig;
}

export function getSetupSummaryLine(result) {
  const parts = [];
  const m1 = result?.module1 || {};
  if (m1.setupB) parts.push("VCP Breakout");
  else if (m1.setupA) parts.push("Pullback to Support");
  else if (m1.setupType === "VCP_BREAKOUT") parts.push("VCP Breakout");
  else if (m1.setupType === "PULLBACK_TO_SUPPORT") parts.push("Pullback to Support");

  const m2 = result?.module2 || {};
  if (m2.setupA?.triggered) parts.push("Double Bottom");
  else if (m2.setupB?.triggered) parts.push("Higher Low");
  else if (m2.setupC?.active) parts.push("Hammer at Support");

  const m3 = result?.module3 || {};
  if (m3.setupA?.triggered) parts.push("Double Top");
  else if (m3.setupB?.triggered) parts.push("Lower High");
  else if (m3.setupC?.active) parts.push("Shooting Star");

  if (!parts.length && result?.module1?.setupType) {
    parts.push(String(result.module1.setupType).replace(/_/g, " "));
  }

  if (result?.cmf != null) parts.push(`CMF ${result.cmf.toFixed(2)}`);
  if (result?.rsi != null) parts.push(`RSI ${result.rsi.toFixed(0)}`);

  return parts.length ? parts.join(" · ") : "—";
}

export function getSetupTypeLabel(result) {
  const sig = String(result?.signal ?? result?.moduleSignal ?? "");
  const m3 = result?.module3 || {};
  const m2 = result?.module2 || {};
  const m1 = result?.module1 || {};
  if (/TOP_/i.test(sig)) {
    if (m3.setupA?.triggered) return "Double top";
    if (m3.setupB?.triggered) return "Lower high";
    if (m3.setupC?.active) return "Shooting star";
  }
  if (/BOTTOM_/i.test(sig)) {
    if (m2.setupA?.triggered) return "Double bottom";
    if (m2.setupB?.triggered) return "Higher low";
    if (m2.setupB?.active) return "Higher low";
    if (m2.setupC?.active) return "Hammer at support";
  }
  if (m1.setupB) return "VCP breakout";
  if (m1.setupA) return "Pullback to support";
  if (m2.setupA?.triggered) return "Double bottom";
  if (m2.setupB?.triggered) return "Higher low";
  if (m2.setupB?.active) return "Higher low";
  if (m2.setupC?.active) return "Hammer at support";
  if (m3.setupA?.triggered) return "Double top";
  if (m3.setupB?.triggered) return "Lower high";
  if (m3.setupC?.active) return "Shooting star";
  if (m1.setupType) return String(m1.setupType).replace(/_/g, " ").toLowerCase();
  return "—";
}

export function getScannerSignalCategory(result) {
  const d = scannerDisplayVerdict(result);
  if (isScoringV2Result(result)) {
    const label = d.verdict || "";
    if (label.includes("CALLS")) return "CALLS";
    if (label.includes("PUTS") || label.includes("TOP FORMING")) return "TOP";
    if (label.includes("BOTTOM")) return "BOTTOM";
    if (label.startsWith("WATCH")) return "WATCH";
    return "WAIT";
  }
  if (d.verdict === "⚡ CONSIDER CALLS") return "CALLS";
  if (d.verdict === "⚠ CONSIDER PUTS" || d.verdict === "⚠ TOP FORMING") return "TOP";
  if (d.verdict === "🔵 BOTTOM CONFIRMED" || /BOTTOM/i.test(d.verdict)) return "BOTTOM";
  if (/WATCH/i.test(d.verdict)) return "WATCH";
  const sig = normalizeScannerSignal(result);
  if (sig === "SWING_LONG") return "CALLS";
  if (sig === "BOTTOM_CONFIRMED" || sig === "BOTTOM_FORMING") return "BOTTOM";
  if (sig === "TOP_CONFIRMED" || sig === "TOP_FORMING") return "TOP";
  const bs = result?.buyScore;
  if (typeof bs === "number" && bs >= 6 && !isExtendedVerdict(result)) return "CALLS";
  if (typeof bs === "number" && bs >= 3) return "WATCH";
  return "WAIT";
}

export function getScannerRowBorderColor(result) {
  const map = {
    CALLS: "#16a34a",
    BOTTOM: "#0284c7",
    TOP: "#ef4444",
    WATCH: "#f59e0b",
    WAIT: "#64748b",
  };
  return map[getScannerSignalCategory(result)] || map.WAIT;
}

export function getScannerDisplayBadge(result) {
  const d = scannerDisplayVerdict(result);
  const a = result?.scannerAction;
  const style =
    a?.tone === "go" ? { bg: "#16a34a", text: "#fff" }
    : a?.tone === "blocked" ? { bg: "#374151", text: "#9ca3af" }
    : a?.tone === "warn" ? { bg: "#f59e0b", text: "#111827" }
    : d.verdictStyle === "bullish" ? { bg: "#16a34a", text: "#fff" }
    : d.verdictStyle === "bearish" ? { bg: "#ef4444", text: "#fff" }
    : d.verdictStyle === "warning" ? { bg: "#f59e0b", text: "#111827" }
    : { bg: "#64748b", text: "#fff" };
  const label = d.verdict.length > 28 ? d.verdict.slice(0, 26) + "…" : d.verdict;
  const reason = result?.reasonLine ?? a?.reason ?? d.subtitle ?? "";
  return { label, reason, tooltip: reason || d.subtitle || "", ...style };
}

export function earningsPillStyle(level, T) {
  if (level === "red") {
    return { color: "#fecaca", background: "#7f1d1d", border: "1px solid #ef4444" };
  }
  if (level === "amber") {
    return { color: "#78350f", background: "#fef3c7", border: "1px solid #f59e0b" };
  }
  return { color: T?.textDim ?? "#94a3b8", background: T?.surfaceHi ?? "#1e293b", border: `1px solid ${T?.border ?? "#334155"}` };
}

export function YesterdayMoversPanel({ T, F }) {
  const [rows, setRows] = useState([]);
  const [sessionDate, setSessionDate] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch("/api/movers-audit?limit=12");
        const j = await r.json();
        if (!cancelled) {
          setRows(j.rows || []);
          setSessionDate(j.sessionDate || null);
        }
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  if (loading && !rows.length) return null;
  if (!rows.length) return null;
  const mono = { fontFamily: F?.mono || "'JetBrains Mono', monospace" };
  return (
    <div
      style={{
        marginBottom: 10,
        padding: "10px 12px",
        borderRadius: 10,
        border: `1px solid ${T.border}`,
        background: T.surface,
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 800, color: T.text, marginBottom: 8 }}>
        Yesterday&apos;s movers
        {sessionDate ? (
          <span style={{ fontWeight: 500, color: T.textDim, marginLeft: 8 }}>{sessionDate}</span>
        ) : null}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 160, overflow: "auto" }}>
        {rows.map((row) => (
          <div key={row.symbol} style={{ fontSize: 10, lineHeight: 1.35, ...mono }}>
            <span style={{ fontWeight: 800, color: T.cyan }}>{row.symbol}</span>
            {" "}
            <span style={{ color: row.move_pct >= 0 ? T.green : T.red }}>
              {row.move_pct >= 0 ? "+" : ""}
              {Number(row.move_pct).toFixed(1)}%
            </span>
            <span style={{ color: T.textDim }}> ({Number(row.move_atr_mult).toFixed(1)}× ATR)</span>
            <div style={{ color: T.textSub, marginTop: 2 }}>
              Prior: {row.prior_verdict || "—"}
              {row.prior_score != null ? ` · score ${row.prior_score}` : ""}
              {row.prior_reason ? ` · ${row.prior_reason}` : ""}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function EarningsRowPill({ result, symbol, T }) {
  const pill = getEarningsRowPill(result, symbol);
  if (!pill) return null;
  const st = earningsPillStyle(pill.level, T);
  return (
    <span
      title={pill.text}
      style={{
        display: "inline-block",
        fontSize: 9,
        fontWeight: 700,
        padding: "2px 5px",
        borderRadius: 5,
        lineHeight: 1.25,
        maxWidth: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        ...st,
      }}
    >
      {pill.text}
    </span>
  );
}

/** Scanner + watchlist subline: waiting reason, else setup type. */
export function getSetupSubLine(result, badge) {
  const a = result?.scannerAction;
  const reason = result?.reasonLine ?? badge?.reason ?? "";
  if (reason && a?.tone !== "go") {
    if (/FORMING/i.test(a?.label || "")) return reason;
    if (a?.tone === "pending" || a?.tone === "blocked") return reason;
  }
  return getSetupTypeLabel(result);
}

/**
 * Gate column = entry readiness (checklist + structural gate), not raw calcGate alone.
 */
export function getScannerGateCell(result, T) {
  const structural = result?.gate?.label ?? "—";
  const action = result?.scannerAction?.label ?? "";
  const mom = result?.momentumChecklist?.status ?? result?.momentum_status;
  const puts = result?.putsChecklist?.status ?? result?.puts_status;

  if (isScoringV2Result(result)) {
    if (/ENTRY OK|TRIGGERED/i.test(action)) {
      return {
        label: "GO",
        color: T.green,
        title: `Entry ready. Structural gate: ${structural}.`,
      };
    }
    if (/FORMING/i.test(action) || mom === "FORMING" || puts === "FORMING") {
      return {
        label: "WAIT",
        color: T.textDim,
        title: `Checklist still forming — not entry-ready. Structural gate (${structural}) is technical context only.`,
      };
    }
    if (
      /SETUP|waiting|not confirmed/i.test(action) ||
      mom === "SETUP" ||
      puts === "SETUP"
    ) {
      if (structural === "BLOCKED") {
        return { label: "BLOCKED", color: T.red, title: "Checklist or structural gate blocked." };
      }
      if (structural === "GO") {
        return {
          label: "SETUP",
          color: T.yellow,
          title: "Checklist pass — confirm gate/R:R before entry. Structural gate: GO.",
        };
      }
      return {
        label: structural,
        color: T.textSub,
        title: `Waiting — structural gate: ${structural}.`,
      };
    }
    if (/BLOCKED/i.test(action)) {
      return {
        label: structural === "BLOCKED" ? "BLOCKED" : structural,
        color: T.red,
        title: result?.reasonLine || "",
      };
    }
  }

  const color =
    structural === "GO" ? T.green : structural === "BLOCKED" ? T.red : T.textSub;
  return { label: structural, color, title: "" };
}

/** Subline + optional Near Prime hint (v2). */
export function getSetupExplanationLine(result, badge) {
  const forming = /FORMING/i.test(result?.scannerAction?.label || "");
  let line = getSetupSubLine(result, badge);
  if (forming) return line;
  if (isScoringV2Result(result) && result?.isPrime !== true) {
    const np = evaluateNearPrimeSetup(result);
    if (np.nearPrime && np.missing?.length) {
      const hint = `Near Prime — ${np.missing.slice(0, 2).join("; ")}`;
      line = line && line !== "—" ? `${line} · ${hint}` : hint;
    }
  }
  return line;
}

export function calcT1Upside(result) {
  const price = result?.price;
  const t1 = result?.t1;
  if (price == null || t1 == null || price <= 0) return null;
  return ((t1 - price) / price) * 100;
}

export function getTrend7d(result) {
  const closes = result?.closes;
  if (!closes?.length || closes.length < 2) return null;
  const prior = closes[Math.min(6, closes.length - 1)];
  if (!prior) return null;
  return ((closes[0] - prior) / prior) * 100;
}

export function applyQuickFilter(results, quickFilter, watchlistSymbols = []) {
  if (!quickFilter || quickFilter === "ALL") return results;
  const wl = new Set((watchlistSymbols || []).map((s) => String(s).toUpperCase()));
  if (quickFilter === "CONSIDER") {
    return results.filter(({ result }) => matchesVerdictProfile(result, "CONSIDER_CALLS"));
  }
  if (quickFilter === "PRIME") {
    return results.filter(({ result }) => result?.isPrime === true);
  }
  if (quickFilter === "NEW") {
    return results.filter(({ symbol }) => !wl.has(String(symbol).toUpperCase()));
  }
  if (quickFilter === "DIPS") {
    return results.filter(({ result }) => {
      const st = result?.dipChecklist?.status ?? result?.dip_status;
      return st === "SETUP" || st === "TRIGGERED";
    });
  }
  return results.filter(({ result }) => getScannerSignalCategory(result) === quickFilter);
}

export function syncQuickFilterToSignalTypes(quickFilter) {
  if (quickFilter === "CALLS" || quickFilter === "CONSIDER" || quickFilter === "PRIME") return ["SWING_LONG"];
  if (quickFilter === "BOTTOM") return ["BOTTOM_CONFIRMED", "BOTTOM_FORMING"];
  if (quickFilter === "TOP") return ["TOP_CONFIRMED", "TOP_FORMING"];
  if (quickFilter === "WAIT") return ["WAIT"];
  if (quickFilter === "WATCH" || quickFilter === "NEW") return ["ALL"];
  return ["ALL"];
}

export function isSectorLeader(symbol) {
  const sym = String(symbol || "").toUpperCase();
  const etf = SECTOR_ROTATION_TICKER_MAP[sym];
  if (!etf) return false;
  return (SECTOR_TICKER_PLAYS[etf] || []).includes(sym);
}

export function matchesScannerFilters(result, filters, { symbol } = {}) {
  if (!result) return false;
  const sig = normalizeScannerSignal(result);

  const types = filters.signalTypes || [];
  if (!types.includes("ALL") && types.length > 0 && !types.includes(sig)) return false;

  const setups = filters.setupTypes || [];
  if (setups.length > 0) {
    const anySetup = SETUP_TYPE_OPTIONS.some(opt => setups.includes(opt.id) && opt.match(result));
    if (!anySetup) return false;
  }

  const v = filters.validation || {};
  if (v.cmf && !(result.cmf > 0.10 || result.module1?.validation?.cmfStrong)) return false;
  if (v.rsi && !(result.rsi >= 45 && result.rsi <= 65)) return false;
  if (v.volume && !(result.rvol?.ratio > 1.5 || result.module1?.validation?.volume)) return false;
  if (v.obv && !(result.obv?.rising || result.module1?.validation?.obv)) return false;
  if (v.preFilter && !result.module1?.preFilterPass) return false;

  const minStrength = filters.minStrength ?? 0;
  if (minStrength > 0) {
    const strength = result.signalStrength;
    if (isScoringV2Result(result)) {
      if (strength == null) {
        /* v2 rows use buyScore/gate, not legacy strength % — do not hide ENTRY OK */
      } else if (strength < minStrength) return false;
    } else if ((strength ?? 0) < minStrength) {
      return false;
    }
  }

  const ctx = filters.context || {};
  if (ctx.minPrice5 && result.price != null && result.price < 5) return false;
  if (ctx.bullTide) {
    const band = result.tideBand || result.marketTide?.breadthScore;
    const score = typeof result.marketTide?.breadthScore === "number" ? result.marketTide.breadthScore : null;
    const bull = result.tideBand === "BULL" || result.tideBand === "STRONG_BULL" || (score != null && score >= 2);
    if (!bull) return false;
  }
  if (ctx.qqqBullish) {
    const qqq = result.marketTide?.qqqPct;
    if (qqq == null || qqq <= 0) return false;
  }
  if (ctx.sectorLeaders && symbol && !isSectorLeader(symbol)) return false;
  if (ctx.excludeEarnings14) {
    const days = result.earningsState?.daysAway ?? result.earningsDaysAway ?? result.scanEarningsDaysAway;
    if (days != null && days >= 0 && days <= 14) return false;
  }
  if (ctx.hideExtended && isExtendedVerdict(result)) return false;
  if (ctx.aboveAllMas && !matchesAboveAllMas(result)) return false;
  if (ctx.testingEma50 && !matchesTestingEma50(result)) return false;

  const profile = filters.verdictProfile;
  if (profile && profile !== "ALL" && !matchesVerdictProfile(result, profile)) return false;

  if (filters.callsChecklistMin != null && filters.callsChecklistMin > 0) {
    const passed = result.callsSignal?.passed ?? 0;
    if (passed < filters.callsChecklistMin) return false;
  }

  return true;
}

export function countBySignalType(results) {
  const counts = { consider: 0, prime: 0, calls: 0, bottom: 0, top: 0, wait: 0 };
  for (const { result } of results) {
    if (matchesVerdictProfile(result, "CONSIDER_CALLS")) counts.consider += 1;
    if (result?.isPrime === true) counts.prime += 1;
    const cat = getScannerSignalCategory(result);
    if (cat === "CALLS") counts.calls += 1;
    else if (cat === "BOTTOM") counts.bottom += 1;
    else if (cat === "TOP") counts.top += 1;
    else counts.wait += 1;
  }
  return counts;
}

function sortValue(entry, sortCol, sectorLabel) {
  const r = entry.result;
  if (sortCol === "symbol") return entry.symbol;
  if (sortCol === "signal" || sortCol === "rank") return rankScannerResult(r);
  if (sortCol === "price") return r?.price ?? -Infinity;
  if (sortCol === "buyScore") return r?.buyScore ?? -Infinity;
  if (sortCol === "maStack") return r?.ma?.pctFrom50 ?? -999;
  if (sortCol === "gate") {
    const cell = getScannerGateCell(r, { green: 1, red: 0, yellow: 1, textDim: 0, textSub: 1 });
    const g = cell.label;
    if (g === "GO") return 4;
    if (g === "SETUP") return 3;
    if (g === "CLOSE") return 2.5;
    if (g === "NEAR") return 2;
    if (g === "BLOCKED") return 0;
    if (g === "WAIT") return 0.5;
    return 1;
  }
  if (sortCol === "rr1") return r?.rr1 ?? -Infinity;
  if (sortCol === "confluence") {
    const cf = watchlistConfluenceCell(r);
    return cf?.text && cf.text !== "—" ? 1 : 0;
  }
  if (sortCol === "confluencePrime") {
    return primeConfluenceSortValue(r);
  }
  if (sortCol === "cmf") return r?.cmf ?? -Infinity;
  if (sortCol === "rsi") return r?.rsi ?? -Infinity;
  if (sortCol === "pctChange") return r?.pctChange ?? -Infinity;
  if (sortCol === "t1Upside") return calcT1Upside(r) ?? -Infinity;
  if (sortCol === "rsDuringDrop") {
    return r?.rsDuringDrop ?? r?.dipChecklist?.rsDuringDrop ?? -Infinity;
  }
  if (sortCol === "trend") return getTrend7d(r) ?? -Infinity;
  if (sortCol === "sector") return sectorLabel ?? "";
  return r?.signalStrength ?? -Infinity;
}

export function sortScannerResults(results, sortCol, sortDir, sectorLabels = {}) {
  const dir = sortDir >= 0 ? 1 : -1;
  return [...results].sort((a, b) => {
    const av = sortValue(a, sortCol, sectorLabels[a.symbol]);
    const bv = sortValue(b, sortCol, sectorLabels[b.symbol]);
    let cmp;
    if (typeof av === "string" && typeof bv === "string") {
      cmp = av.localeCompare(bv) * dir;
    } else {
      cmp = ((Number(bv) ?? -Infinity) - (Number(av) ?? -Infinity)) * dir;
    }
    if (cmp === 0 && (sortCol === "signal" || sortCol === "rank")) {
      cmp =
        ((Number(b.result?.buyScore) ?? -Infinity) -
          (Number(a.result?.buyScore) ?? -Infinity)) *
        dir;
    }
    if (cmp === 0) {
      cmp = String(a.symbol).localeCompare(String(b.symbol));
    }
    return cmp;
  });
}

export function filterTickersBySector(tickers, sectorName) {
  return filterTickersByRotationSector(tickers, sectorName);
}

export { SCANNER_PRESETS, applyScannerPreset };

function MiniSparkline({ closes, color }) {
  const slice = (closes || []).slice(0, 7).reverse();
  if (slice.length < 2) return <span style={{ color: "#64748b", fontSize: 10 }}>—</span>;
  const min = Math.min(...slice);
  const max = Math.max(...slice);
  const range = max - min || 1;
  const w = 56;
  const h = 22;
  const pts = slice.map((v, i) => {
    const x = (i / (slice.length - 1)) * w;
    const y = h - ((v - min) / range) * (h - 4) - 2;
    return `${x},${y}`;
  }).join(" ");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: "block" }}>
      <polyline fill="none" stroke={color} strokeWidth="1.5" points={pts} />
    </svg>
  );
}

const selectStyle = (T, F) => ({
  padding: "5px 8px",
  background: T.surfaceHi,
  border: `1px solid ${T.border}`,
  color: T.text,
  borderRadius: 6,
  fontSize: F.xs,
  fontFamily: "inherit",
  cursor: "pointer",
});

export function ScannerCompactFilterBar({
  filters,
  setFilters,
  sortCol,
  onSortChange,
  themeOptions = ["All Themes"],
  T,
  F,
}) {
  const quick = filters.quickFilter || "ALL";
  const primeActive = quick === QUICK_FILTER_PRIME_ID;
  const pillStyle = (id, active, gold = false) => ({
    padding: "5px 12px",
    background: active
      ? (gold ? "#f59e0b22" : `${T.cyan}22`)
      : T.surfaceHi,
    border: `1px solid ${
      active
        ? (gold ? "#f59e0b" : `${T.cyan}66`)
        : (gold ? "#f59e0b55" : T.border)
    }`,
    color: active ? (gold ? "#fbbf24" : T.cyan) : (gold ? "#d97706" : T.textDim),
    fontSize: F.xs,
    fontWeight: active ? 700 : 500,
    fontFamily: "inherit",
    cursor: "pointer",
    borderRadius: 16,
  });

  const setQuick = (id) => {
    setFilters(f => ({
      ...f,
      quickFilter: id,
      signalTypes: syncQuickFilterToSignalTypes(id),
    }));
    if (id === QUICK_FILTER_PRIME_ID) {
      onSortChange("confluencePrime", -1);
    }
  };

  return (
    <div style={{
      display: "flex",
      flexWrap: "wrap",
      gap: 8,
      alignItems: "center",
      padding: "10px 12px",
      background: T.bg,
      border: `1px solid ${T.border}`,
      borderRadius: 10,
      flexShrink: 0,
    }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
        {QUICK_FILTER_LEAD.map((opt) => (
          <button key={opt.id} type="button" onClick={() => setQuick(opt.id)} style={pillStyle(opt.id, quick === opt.id)}>
            {opt.label}
          </button>
        ))}
        <span style={{ width: 1, height: 18, background: T.border, margin: "0 2px" }} aria-hidden />
        {QUICK_FILTER_SIGNAL.map((opt) => (
          <button key={opt.id} type="button" onClick={() => setQuick(opt.id)} style={pillStyle(opt.id, quick === opt.id)}>
            {opt.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setQuick(QUICK_FILTER_PRIME_ID)}
          style={pillStyle(QUICK_FILTER_PRIME_ID, primeActive, true)}
          title="Gate GO + structure + accumulation + momentum + entry precision"
        >
          ⭐ Prime
        </button>
        <span style={{ width: 1, height: 18, background: T.border, margin: "0 2px" }} aria-hidden />
        <button
          type="button"
          onClick={() => setFilters((f) => ({
            ...f,
            context: { ...f.context, aboveAllMas: !f.context?.aboveAllMas, testingEma50: false },
          }))}
          style={pillStyle("aboveAllMas", !!filters.context?.aboveAllMas)}
          title="Price above EMA20/50/200 and all three rising (5d slope)"
        >
          Above all MAs
        </button>
        <button
          type="button"
          onClick={() => setFilters((f) => ({
            ...f,
            context: { ...f.context, testingEma50: !f.context?.testingEma50, aboveAllMas: false },
          }))}
          style={pillStyle("testingEma50", !!filters.context?.testingEma50)}
          title="Within ±2% of EMA50 with EMA50 rising"
        >
          Testing 50-day
        </button>
      </div>

      <select
        value={filters.minStrength ?? 50}
        onChange={e => setFilters(f => ({ ...f, minStrength: Number(e.target.value) }))}
        style={selectStyle(T, F)}
        title="Min Strength"
      >
        {MIN_STRENGTH_OPTIONS.map(o => (
          <option key={o.value} value={o.value}>Min Strength: {o.label}</option>
        ))}
      </select>

      <select
        value={filters.universeMode || "core"}
        onChange={e => setFilters(f => ({ ...f, universeMode: e.target.value }))}
        style={{ ...selectStyle(T, F), minWidth: 100 }}
        title="Core = fast rotation universe · Full = entire curated list"
      >
        <option value="core">Core (fast)</option>
        <option value="full">Full universe</option>
      </select>

      <select
        value={filters.sector || "All Sectors"}
        onChange={e => setFilters(f => ({ ...f, sector: e.target.value }))}
        style={{ ...selectStyle(T, F), minWidth: 120 }}
        title="Rotation sector (XLK, XLV, …)"
      >
        {SCANNER_SECTOR_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
      </select>

      <select
        value={filters.theme || "All Themes"}
        onChange={e => setFilters(f => ({ ...f, theme: e.target.value }))}
        style={{ ...selectStyle(T, F), minWidth: 130 }}
        title="Theme bucket from scanner universe"
      >
        {themeOptions.map(s => <option key={s} value={s}>{s}</option>)}
      </select>

      <select
        value={sortCol}
        onChange={e => onSortChange(e.target.value, -1)}
        style={selectStyle(T, F)}
      >
        {SCANNER_SORT_OPTIONS.map(o => (
          <option key={o.col} value={o.col}>Sort: {o.label}</option>
        ))}
      </select>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, width: "100%", marginTop: 4 }}>
        {Object.entries(SCANNER_PRESETS).map(([id, p]) => (
          <button
            key={id}
            type="button"
            onClick={() => setFilters(f => ({ ...f, ...applyScannerPreset(id) }))}
            style={{
              padding: "4px 10px",
              background: T.surfaceHi,
              border: `1px solid ${T.border}`,
              color: T.textSub,
              fontSize: 10,
              fontWeight: 700,
              fontFamily: "inherit",
              cursor: "pointer",
              borderRadius: 14,
            }}
          >
            {p.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function ScannerResultsTable({
  rows,
  companyNames,
  getSectorLabel,
  watchlistSymbols,
  onAddToWatchlist,
  onRowClick,
  sortCol,
  sortDir,
  onSortChange,
  totalCount,
  scrollResetKey,
  T,
  F,
}) {
  const SCROLL_BATCH = 50;
  const mono = { fontFamily: "'JetBrains Mono', monospace" };
  const scrollRef = React.useRef(null);
  const [visibleCount, setVisibleCount] = React.useState(SCROLL_BATCH);

  React.useEffect(() => {
    setVisibleCount(SCROLL_BATCH);
  }, [scrollResetKey]);

  const visibleRows = rows.slice(0, visibleCount);
  const shownCount = visibleRows.length;
  const hasMore = shownCount < rows.length;

  const loadMore = React.useCallback(() => {
    setVisibleCount(c => Math.min(rows.length, c + SCROLL_BATCH));
  }, [rows.length]);

  const handleScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 200) {
      loadMore();
    }
  };

  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el || !hasMore) return;
    if (el.scrollHeight <= el.clientHeight + 200) {
      loadMore();
    }
  }, [shownCount, hasMore, loadMore]);

  const handleHeaderClick = (col) => {
    if (!col.sortable) return;
    if (sortCol === col.id) onSortChange(col.id, -sortDir);
    else onSortChange(
      col.id,
      col.id === "rsi" || col.id === "symbol" || col.id === "sector" ? 1 : -1
    );
  };

  const thStyle = (col) => ({
    padding: "10px 12px",
    textAlign: col.id === "add" ? "right" : "left",
    fontSize: 11,
    fontWeight: 800,
    color: sortCol === col.id ? T.cyan : T.textDim,
    letterSpacing: "0.06em",
    borderBottom: `1px solid ${T.border}`,
    background: T.surface,
    cursor: col.sortable ? "pointer" : "default",
    whiteSpace: "nowrap",
    userSelect: "none",
    width: col.width,
    minWidth: col.width,
  });

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        style={{ flex: 1, overflow: "auto", border: `1px solid ${T.border}`, borderRadius: 10 }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
          <thead>
            <tr>
              {SCANNER_TABLE_COLUMNS.map(col => (
                <th key={col.id} style={thStyle(col)} onClick={() => handleHeaderClick(col)}>
                  {col.label}
                  {col.sortable && sortCol === col.id ? (sortDir === -1 ? " ▼" : " ▲") : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map(({ symbol, result }) => {
              const borderColor = getScannerRowBorderColor(result);
              const badge = getScannerDisplayBadge(result);
              const verdictMeta = scannerDisplayVerdict(result);
              const subLine = getSetupExplanationLine(result, badge);
              const price = result?.price;
              const pct = result?.pctChange ?? 0;
              const cmf = result?.cmf;
              const rsi = result?.rsi;
              const sectorLabel = getSectorLabel?.(symbol) || "—";
              const t1 = result?.t1;
              const t1Upside = calcT1Upside(result);
              const inWl = watchlistSymbols?.includes(symbol);
              const gateCell = getScannerGateCell(result, T);
              const cf = watchlistConfluenceCell(result);
              const bs = result?.buyScore;

              return (
                <tr
                  key={symbol}
                  onClick={() => onRowClick?.(symbol, result)}
                  style={{
                    borderBottom: `1px solid ${T.border}`,
                    borderLeft: `3px solid ${borderColor}`,
                    cursor: "pointer",
                    background: "transparent",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = T.surfaceHi; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                >
                  <td style={{ padding: "8px 10px", verticalAlign: "middle" }}>
                    <div style={{ fontSize: 13, fontWeight: 800, ...mono, color: T.text }}>{symbol}</div>
                    <div style={{ fontSize: 10, color: T.textDim, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {companyNames[symbol] || symbol}
                    </div>
                  </td>
                  <td style={{ padding: "8px 10px", verticalAlign: "middle" }}>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
                      <span
                        title={badge.tooltip || ""}
                        style={{
                        display: "inline-block",
                        fontSize: 10,
                        fontWeight: 800,
                        color: badge.text,
                        background: badge.bg,
                        padding: "3px 6px",
                        borderRadius: 5,
                        lineHeight: 1.25,
                      }}>
                        {badge.label}
                      </span>
                      <EarningsRowPill result={result} symbol={symbol} T={T} />
                      {result?.isPrime === true && (
                        <span
                          title={(result.primeReasons || []).join(" · ") || "Prime setup"}
                          style={{
                            display: "inline-block",
                            fontSize: 9,
                            fontWeight: 800,
                            color: "#78350f",
                            background: "linear-gradient(180deg, #fef3c7 0%, #fde68a 100%)",
                            border: "1px solid #f59e0b",
                            padding: "2px 5px",
                            borderRadius: 5,
                            lineHeight: 1.25,
                            whiteSpace: "nowrap",
                          }}
                        >
                          ⭐ PRIME
                        </span>
                      )}
                      {result?.isPrime !== true && isScoringV2Result(result) && (() => {
                        const np = evaluateNearPrimeSetup(result);
                        if (!np.nearPrime) return null;
                        return (
                          <span
                            title={`Missing for Prime: ${np.missing.join("; ")}`}
                            style={{
                              display: "inline-block",
                              fontSize: 9,
                              fontWeight: 800,
                              color: "#a16207",
                              background: "#fef9c322",
                              border: "1px solid #eab30866",
                              padding: "2px 5px",
                              borderRadius: 5,
                              lineHeight: 1.25,
                              whiteSpace: "nowrap",
                            }}
                          >
                            ◇ Near Prime
                          </span>
                        );
                      })()}
                      {(() => {
                        const ltPill = longTermStatusPill(result?.longTerm);
                        if (!ltPill || ltPill.label.includes("TRAP")) return null;
                        return (
                          <span
                            title={(result?.longTerm?.discountReasons || []).join(" · ")}
                            style={{
                              display: "inline-block",
                              fontSize: 9,
                              fontWeight: 800,
                              color: ltPill.color,
                              background: `${ltPill.color}18`,
                              border: `1px solid ${ltPill.color}55`,
                              padding: "2px 5px",
                              borderRadius: 5,
                              lineHeight: 1.25,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {ltPill.label}
                          </span>
                        );
                      })()}
                    </div>
                    <div style={{ fontSize: 9, color: T.textDim, marginTop: 3 }} title={badge.tooltip || subLine}>{subLine}</div>
                  </td>
                  <td style={{ padding: "8px 10px", verticalAlign: "middle", textAlign: "center" }}>
                    <div style={{
                      fontSize: 13,
                      fontWeight: 800,
                      ...mono,
                      color: verdictMeta.scoreGrey ? T.textDim : bs >= 6 ? T.green : bs <= -6 ? T.red : T.yellow,
                    }}>
                      {bs != null ? `${bs > 0 ? "+" : ""}${bs}` : "—"}
                      {verdictMeta.scoreSuffix ? (
                        <span style={{ fontSize: 8, display: "block", color: T.textDim }}>{verdictMeta.scoreSuffix}</span>
                      ) : null}
                    </div>
                  </td>
                  <td style={{ padding: "8px 10px", verticalAlign: "middle", textAlign: "center" }}>
                    <span
                      title={gateCell.title || undefined}
                      style={{ fontSize: 10, fontWeight: 800, color: gateCell.color, cursor: gateCell.title ? "help" : "default" }}
                    >
                      {gateCell.label}
                    </span>
                  </td>
                  <td style={{ padding: "6px 4px", verticalAlign: "middle", textAlign: "center" }}>
                    <MaStackPills result={result} T={T} vertical />
                  </td>
                  <td style={{ padding: "8px 10px", verticalAlign: "middle", textAlign: "center" }}>
                    <span style={{ fontSize: 10, fontWeight: 700, ...mono, color: result?.rr1 != null && result.rr1 >= 1.5 ? T.green : T.textSub }}>
                      {result?.rr1 != null ? result.rr1.toFixed(2) : "—"}
                    </span>
                  </td>
                  <td style={{ padding: "8px 10px", verticalAlign: "middle", textAlign: "center" }}>
                    <span style={{ fontSize: 10, fontWeight: 800, color: cf.text !== "—" ? T.cyan : T.textDim }}>{cf.text}</span>
                  </td>
                  <td style={{ padding: "8px 10px", verticalAlign: "middle" }}>
                    <div style={{ fontSize: 12, fontWeight: 700, ...mono, color: T.text }}>
                      {price != null ? `$${price.toFixed(2)}` : "—"}
                    </div>
                    <div style={{ fontSize: 10, fontWeight: 600, ...mono, color: pct >= 0 ? T.green : T.red, marginTop: 2 }}>
                      {price != null ? `${pct >= 0 ? "+" : ""}${pct.toFixed(2)}%` : "—"}
                    </div>
                  </td>
                  <td style={{ padding: "8px 10px", verticalAlign: "middle", textAlign: "center" }}>
                    <div style={{
                      fontSize: 11,
                      fontWeight: 700,
                      ...mono,
                      color: cmf != null && cmf > 0.10 ? T.green : cmf != null && cmf < 0 ? T.red : T.text,
                    }}>
                      {cmf != null ? cmf.toFixed(2) : "—"}
                    </div>
                  </td>
                  <td style={{ padding: "8px 10px", verticalAlign: "middle", textAlign: "center" }}>
                    <div style={{ fontSize: 12, fontWeight: 700, ...mono, color: T.text }}>
                      {rsi != null ? rsi.toFixed(0) : "—"}
                    </div>
                  </td>
                  <td style={{ padding: "8px 10px", verticalAlign: "middle" }}>
                    <span style={{
                      display: "inline-block",
                      fontSize: 10,
                      fontWeight: 700,
                      padding: "2px 5px",
                      borderRadius: 999,
                      background: `${T.cyan}14`,
                      border: `1px solid ${T.cyan}33`,
                      color: T.textSub,
                      maxWidth: "100%",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}>
                      {sectorLabel}
                    </span>
                  </td>
                  <td style={{ padding: "8px 10px", verticalAlign: "middle" }}>
                    {t1 != null ? (
                      <>
                        <div style={{ fontSize: 11, fontWeight: 700, ...mono, color: T.green }}>${t1.toFixed(2)}</div>
                        <div style={{ fontSize: 9, color: T.textDim, marginTop: 2 }}>
                          {t1Upside != null ? `+${t1Upside.toFixed(1)}%` : "—"}
                        </div>
                      </>
                    ) : (
                      <span style={{ color: T.textDim, fontSize: 11 }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: "8px 10px", verticalAlign: "middle", textAlign: "right" }}>
                    {inWl ? (
                      <span style={{ fontSize: 11, color: T.green, fontWeight: 700 }} title="On watchlist">✓</span>
                    ) : (
                      <button
                        onClick={(e) => { e.stopPropagation(); onAddToWatchlist?.(symbol); }}
                        style={{
                          padding: "4px 7px",
                          background: `${T.cyan}18`,
                          border: `1px solid ${T.cyan}44`,
                          color: T.cyan,
                          fontSize: 10,
                          fontWeight: 700,
                          fontFamily: "inherit",
                          cursor: "pointer",
                          borderRadius: 5,
                        }}
                      >
                        + Add
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalCount > 0 && (
        <div style={{
          padding: "8px 4px",
          flexShrink: 0,
          textAlign: "center",
          fontSize: F.xs,
          color: T.textDim,
        }}>
          Showing {shownCount} of {totalCount}
        </div>
      )}
    </div>
  );
}

export function ScannerResultsBar({ total, counts, T, F }) {
  const pill = (label, n, color) => (
    <span style={{
      fontSize: 11,
      fontWeight: 700,
      padding: "3px 8px",
      borderRadius: 999,
      background: `${color}18`,
      border: `1px solid ${color}44`,
      color,
    }}>
      {n} {label}
    </span>
  );

  return (
    <div style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 8,
      padding: "8px 4px",
      flexShrink: 0,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 13, fontWeight: 800, color: T.text }}>{total} results</span>
        {pill("Consider", counts.consider ?? 0, "#16a34a")}
        {pill("Prime", counts.prime ?? 0, "#eab308")}
        {pill("Bottom", counts.bottom, "#0284c7")}
        {pill("Top", counts.top, "#ef4444")}
        {pill("Wait", counts.wait, "#64748b")}
      </div>
    </div>
  );
}

export function ScannerFilterPanel({ filters, setFilters, T, F, collapsed, onToggleCollapse }) {
  const toggleSignal = (id) => {
    setFilters(f => {
      if (id === "ALL") return { ...f, signalTypes: ["ALL"] };
      const cur = (f.signalTypes || []).filter(x => x !== "ALL");
      const next = cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id];
      return { ...f, signalTypes: next.length ? next : ["ALL"] };
    });
  };

  const toggleSetup = (id) => {
    setFilters(f => {
      const cur = f.setupTypes || [];
      const next = cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id];
      return { ...f, setupTypes: next };
    });
  };

  const toggleValidation = (key) => {
    setFilters(f => ({
      ...f,
      validation: { ...f.validation, [key]: !f.validation?.[key] },
    }));
  };

  const toggleContext = (key) => {
    setFilters(f => ({
      ...f,
      context: { ...f.context, [key]: !f.context?.[key] },
    }));
  };

  const sectionLabel = (text) => (
    <div style={{ fontSize: 9, fontWeight: 800, color: T.textDim, letterSpacing: "0.08em", marginBottom: 6, marginTop: 10 }}>{text}</div>
  );

  const chip = (active, onClick, children) => (
    <button
      onClick={onClick}
      style={{
        padding: "4px 10px",
        background: active ? `${T.cyan}22` : T.surfaceHi,
        border: `1px solid ${active ? T.cyan + "66" : T.border}`,
        color: active ? T.cyan : T.textDim,
        fontSize: F.xs,
        fontFamily: "inherit",
        cursor: "pointer",
        borderRadius: 16,
        fontWeight: active ? 700 : 500,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </button>
  );

  const check = (checked, onChange, label) => (
    <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: F.xs, color: T.textSub, cursor: "pointer", marginBottom: 4 }}>
      <input type="checkbox" checked={!!checked} onChange={onChange} style={{ accentColor: T.cyan }} />
      {label}
    </label>
  );

  return (
    <div style={{
      background: T.bg,
      border: `1px solid ${T.border}`,
      borderRadius: 10,
      padding: collapsed ? "8px 12px" : "12px 14px",
      flexShrink: 0,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <div style={{ fontSize: F.xs, fontWeight: 800, color: T.textSub, letterSpacing: "0.06em" }}>SCANNER FILTERS</div>
        <button
          onClick={onToggleCollapse}
          style={{ background: "none", border: "none", color: T.textDim, fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}
        >
          {collapsed ? "Show ▼" : "Hide ▲"}
        </button>
      </div>

      {!collapsed && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "4px 16px" }}>
          <div>
            {sectionLabel("SECTION 1 — SIGNAL TYPE")}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
              {SIGNAL_TYPE_OPTIONS.map(opt => chip(
                (filters.signalTypes || []).includes(opt.id) || (opt.id === "ALL" && (filters.signalTypes || []).includes("ALL")),
                () => toggleSignal(opt.id),
                <>{opt.emoji ? `${opt.emoji} ` : "○ "}{opt.label}</>
              ))}
            </div>
          </div>

          <div>
            {sectionLabel("SECTION 2 — SETUP TYPE")}
            {SETUP_TYPE_OPTIONS.map(opt => check(
              (filters.setupTypes || []).includes(opt.id),
              () => toggleSetup(opt.id),
              opt.label
            ))}
          </div>

          <div>
            {sectionLabel("SECTION 3 — VALIDATION FILTERS")}
            {check(filters.validation?.cmf, () => toggleValidation("cmf"), "CMF > 0.10 (institutional buying)")}
            {check(filters.validation?.rsi, () => toggleValidation("rsi"), "RSI in 45-65 range")}
            {check(filters.validation?.volume, () => toggleValidation("volume"), "Volume > 1.5x average")}
            {check(filters.validation?.obv, () => toggleValidation("obv"), "OBV rising")}
            {check(filters.validation?.preFilter, () => toggleValidation("preFilter"), "Pre-filter pass (Price > EMA50 > EMA200)")}
            {check(filters.context?.aboveAllMas, () => toggleContext("aboveAllMas"), "Above all MAs (price + rising slopes)")}
            {check(filters.context?.testingEma50, () => toggleContext("testingEma50"), "Testing 50-day EMA (±2%, rising)")}
          </div>

          <div>
            {sectionLabel("SECTION 4 — SIGNAL STRENGTH")}
            <div style={{ fontSize: F.xs, color: T.textSub, marginBottom: 6 }}>
              Only show signals above {filters.minStrength ?? 50}%
            </div>
            <input
              type="range"
              min={0}
              max={100}
              value={filters.minStrength ?? 50}
              onChange={e => setFilters(f => ({ ...f, minStrength: Number(e.target.value) }))}
              style={{ width: "100%", accentColor: T.cyan }}
            />
          </div>

          <div>
            {sectionLabel("SECTION 5 — MARKET CONTEXT")}
            {check(filters.context?.bullTide, () => toggleContext("bullTide"), "Only scan in Bull Tide")}
            {check(filters.context?.qqqBullish, () => toggleContext("qqqBullish"), "Only scan when QQQ bullish")}
            {check(filters.context?.sectorLeaders, () => toggleContext("sectorLeaders"), "Only scan sector leaders")}
            {check(filters.context?.excludeEarnings14, () => toggleContext("excludeEarnings14"), "Exclude earnings within 14 days")}
            {check(filters.context?.minPrice5, () => toggleContext("minPrice5"), "Only stocks above $5")}
          </div>

          <div>
            {sectionLabel("SECTION 6 — SECTOR FILTER")}
            <select
              value={filters.sector || "All Sectors"}
              onChange={e => setFilters(f => ({ ...f, sector: e.target.value }))}
              style={{
                width: "100%",
                padding: "6px 8px",
                background: T.surfaceHi,
                border: `1px solid ${T.border}`,
                color: T.text,
                borderRadius: 6,
                fontSize: F.xs,
                fontFamily: "inherit",
              }}
            >
              {SCANNER_SECTOR_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}

export function useScannerCompanyNames(symbols) {
  const [names, setNames] = useState({});
  useEffect(() => {
    if (!symbols?.length) return;
    let cancelled = false;
    (async () => {
      const out = {};
      await Promise.all(symbols.slice(0, 80).map(async sym => {
        try {
          const res = await fetch(`/api/quote?symbol=${encodeURIComponent(sym)}`);
          if (res.ok) {
            const data = await res.json();
            if (data.name) out[sym] = data.name;
          }
        } catch {}
      }));
      if (!cancelled) setNames(prev => ({ ...prev, ...out }));
    })();
    return () => { cancelled = true; };
  }, [symbols?.join(",")]);
  return names;
}
